import React from 'react';
import { useMenuAttention } from './menuAttentionStore.ts';

/** Length of one sweep + pause. Must match `animation.menuSheen` in tailwind.config.js. */
const CYCLE_MS = 7000;

/**
 * The attention sheen: a narrow, brand-tinted light that crosses a menu row once every few seconds
 * and then waits off-screen — the pause is what keeps it decent rather than a spinner.
 *
 * Rendered by a row that was declared `attention: true`; the master switch is read here rather than
 * by every caller, so silencing the effect is one setting instead of a flag threaded through the
 * menu. Decoration only: it takes no clicks, and is dropped entirely for anyone who asked the system
 * for less motion.
 *
 * The cycle is anchored to the wall clock with a negative delay rather than starting at mount: the
 * layout — and with it the whole menu — remounts on every navigation, so a mount-relative animation
 * replayed the sweep on each page the user opened, which read as a permanent shimmer. With the
 * offset the sweep happens every CYCLE_MS no matter how much the user clicks around.
 *
 * The parent row must be `relative` and clip its overflow, and must render this **last**: menu icons
 * exported from Figma (ai-robot.svg) paint with `mix-blend-mode: difference`, which blends against
 * whatever was painted beneath them. Underneath the icon, the sheen would join that backdrop and
 * shift the glyph's colours as it passed; painted on top it is just a 10% wash and the icon keeps
 * blending against the plain row.
 */
const MenuAttentionSheen: React.FC = () => {
  const enabled = useMenuAttention();
  if (!enabled) return null;

  return (
    <span
      aria-hidden={true}
      style={{ animationDelay: `-${Date.now() % CYCLE_MS}ms` }}
      className={
        'pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-content-brand/10 to-transparent animate-menuSheen motion-reduce:hidden'
      }
    />
  );
};

export default MenuAttentionSheen;
